import { useMemo } from 'react';
import { FeatureReadinessOrg } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/feature_readiness.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useAppScopeStore } from '@frontend/scope';
import { ListOrgFeatureReadinessIO } from '../types';

export interface UseOnlineSchedulingFeatureReadinessReturn {
  /** Array of location IDs where online scheduling is ready/enabled */
  readyLocationIds: string[];
  /** Array of location IDs where online scheduling is not ready/enabled */
  notReadyLocationIds: string[];
  /** Boolean indicating if any location has online scheduling enabled */
  hasOnlineSchedulingAnywhere: boolean;
  /** Query loading state */
  isLoading: boolean;
  /** Query error state */
  error: unknown | null;
  /** Raw feature readiness data */
  data: unknown;
}

/**
 * Hook to get online scheduling feature readiness across organization locations.
 *
 * Returns information about which locations have the HAS_ONLINE_SCHEDULING feature ready/enabled,
 * filtered to only include the user's selected locations.
 *
 * @param orgId - The organization ID to query feature readiness for (defaults to app scope org)
 * @param selectedLocationIds - Array of location IDs to filter results by (defaults to app scope selected locations)
 * @param options - Optional React Query options
 * @returns Object containing readyLocationIds, notReadyLocationIds (filtered by selected locations), and other query state
 *
 */
export const useOnlineSchedulingFeatureReadiness = ({
  orgId,
  selectedLocationIds,
  options,
}: {
  orgId?: string;
  selectedLocationIds?: string[];
  options?: Parameters<typeof SchedulerV3.Queries.useListOrgFeatureReadinessQuery>[0]['options'];
} = {}): UseOnlineSchedulingFeatureReadinessReturn => {
  const { selectedOrgId: scopeSelectedOrgId, selectedLocationIds: scopeSelectedLocationIds } = useAppScopeStore();
  const resolvedOrgId = orgId ?? scopeSelectedOrgId;
  const resolvedSelectedLocationIds = selectedLocationIds ?? scopeSelectedLocationIds;

  const query = SchedulerV3.Queries.useListOrgFeatureReadinessQuery({
    request: {
      orgId: resolvedOrgId,
      names: ['HAS_ONLINE_SCHEDULING'],
      includeDetails: true,
    },
    options,
  });

  const result = useMemo(() => {
    const data = query.data as ListOrgFeatureReadinessIO['output'] | undefined;
    const feature = data?.features?.[0];
    const orgDetails: FeatureReadinessOrg | undefined = feature && 'org' in feature ? feature.org : undefined;
    const allReadyLocationIds = orgDetails?.readyLocationIds ?? [];
    const allNotReadyLocationIds = orgDetails?.notReadyLocationIds ?? [];

    // Filter to only include selected locations
    const selectedLocationIdSet = new Set(resolvedSelectedLocationIds);
    const readyLocationIds = allReadyLocationIds.filter((id: string) => selectedLocationIdSet.has(id));
    const notReadyLocationIds = allNotReadyLocationIds.filter((id: string) => selectedLocationIdSet.has(id));
    const hasOnlineSchedulingAnywhere = (readyLocationIds ?? []).length > 0;

    return {
      readyLocationIds,
      notReadyLocationIds,
      hasOnlineSchedulingAnywhere,
      isLoading: query.isLoading,
      error: query.error,
      data: query.data,
    };
  }, [query.data, query.isLoading, query.error, resolvedSelectedLocationIds]);

  return result;
};
